import React, { useEffect, useState } from "react";
import { api, formatKES } from "../lib/api";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Wallet, ShieldCheck, AlertTriangle, RefreshCw, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";

/**
 * Escrow widget rendered on the ContractDetail page.
 * - Employer: pays the month upfront, sees released vs unspent balance.
 * - Staff: sees what's been released to them so far and the guarantee promise.
 * - Admin: can trigger the prorated refund after a termination.
 */
export default function EscrowPanel({ contract, currentUser, features, onContractUpdate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const isEmployer = currentUser?.role === "customer" && contract.customer_id === currentUser.id;
  const isStaff = currentUser?.role === "employee" && contract.employee_user_id === currentUser.id;
  const isAdmin = currentUser?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/${contract.id}/escrow`);
      setEntries(data);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);

  // Compute the monthly amount from the contract.
  const monthlyAmount = (() => {
    const s = Number(contract.salary_agreed || 0);
    if (contract.pay_frequency === "monthly") return s;
    if (contract.pay_frequency === "daily") return s * 26;
    return s * 8 * 26;
  })();

  const nextMonthStart = new Date();
  nextMonthStart.setDate(1);
  const nextMonthISO = nextMonthStart.toISOString().slice(0, 10);

  // Has the current month already been funded?
  const currentMonthFunded = entries.some((e) => e.month_start === nextMonthISO && ["active", "terminated", "refunded"].includes(e.status));

  const payMonth = async ({ phone }) => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/escrow/pay-month`, { phone, month_start: nextMonthISO });
      toast.success("Month funded into KSB escrow");
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Escrow deposit failed");
    }
    setBusy(false);
  };

  const terminate = async () => {
    if (!reason.trim()) return toast.error("Add a termination reason");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/terminate`, { reason });
      toast.success("Contract terminated. KSB guarantee window has started.");
      setTermOpen(false);
      setReason("");
      onContractUpdate?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to terminate");
    }
    setBusy(false);
  };

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/complete`, { notes: completionNotes });
      toast.success("Contract marked as completed. Any unspent escrow can be refunded.");
      setCompleteOpen(false);
      setCompletionNotes("");
      onContractUpdate?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    }
    setBusy(false);
  };

  const refund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/escrow/refund`, {});
      toast.success(`Refund of ${formatKES(data.total_refund_kes)} initiated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
    setBusy(false);
  };

  const active = entries.find((e) => e.status === "active");

  return (
    <div className="mt-10 pt-8 border-t border-[#E4E7F5]" data-testid="escrow-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Monthly Escrow &amp; KSB Guarantee
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Salary held safely in KSB escrow</h2>
          <p className="text-[#5B6478] text-sm mt-1 max-w-2xl">
            Employers pay the full month upfront. KSB releases the daily-equivalent to the staff each working day.
            When the engagement ends the unspent balance is refunded prorated —
            <strong className="text-[#0F1E4B]"> Completed</strong> (proper notice served) settles immediately;
            <strong className="text-[#0F1E4B]"> Terminated</strong> (premature end) opens a {features?.guarantee_window_days || 14}-day KSB replacement window first.
          </p>
        </div>
        {isEmployer && contract.status === "active" && !currentMonthFunded && (
          <Button
            data-testid="escrow-pay-btn"
            onClick={() => setPayOpen(true)}
            className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white gap-2"
          >
            <Wallet className="w-4 h-4" /> Pay this month · {formatKES(monthlyAmount)}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="h-24 bg-[#EEF0FA] rounded animate-pulse" />
      ) : entries.length === 0 ? (
        <div className="bg-[#EEF0FA] border border-[#E4E7F5] rounded-md p-5 text-sm text-[#5B6478]">
          No escrow deposits yet. {isEmployer && contract.status === "active" && "Fund the first month to activate the KSB guarantee."}
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => {
            const pct = e.amount_deposited > 0 ? Math.min(100, Math.round((e.amount_released_computed / e.amount_deposited) * 100)) : 0;
            return (
              <div key={e.id} data-testid={`escrow-entry-${e.id}`} className="bg-white border border-[#E4E7F5] rounded-md p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display font-bold text-[#0F1E4B]">
                      {new Date(e.month_start).toLocaleDateString("en-KE", { month: "long", year: "numeric" })}
                    </div>
                    <div className="text-xs text-[#5B6478] mt-0.5">
                      {e.month_start} → {e.month_end} · Daily rate {formatKES(e.daily_rate)}
                    </div>
                  </div>
                  <StatusPill status={e.status} />
                </div>
                <Progress value={pct} className="mt-4 h-2" />
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <Field label="Deposited" value={formatKES(e.amount_deposited)} />
                  <Field label="Released to staff" value={formatKES(e.amount_released_computed)} />
                  <Field label="Unspent balance" value={formatKES(e.amount_unspent)} highlight={e.status === "terminated"} />
                  <Field label="Days elapsed" value={`${e.days_elapsed} / ${e.days_total}`} />
                </div>
                {e.status === "refunded" && (
                  <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Refunded {formatKES(e.amount_refunded)} · Receipt {e.refund_receipt} · KSB commission kept {formatKES(e.ksb_commission_kept || 0)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Guarantee actions */}
      {(isEmployer || isStaff || isAdmin) && contract.status === "active" && active && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            data-testid="contract-complete-btn"
            variant="outline"
            onClick={() => setCompleteOpen(true)}
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white gap-2"
          >
            <CheckCircle2 className="w-4 h-4" /> Mark as completed
          </Button>
          <Button
            data-testid="contract-terminate-btn"
            variant="outline"
            onClick={() => setTermOpen(true)}
            className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white gap-2"
          >
            <XCircle className="w-4 h-4" /> Terminate early
          </Button>
        </div>
      )}
      {contract.status === "completed" && (isEmployer || isAdmin) && entries.some((e) => e.status === "completed") && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-emerald-900">Contract completed with proper notice</div>
            <div className="text-xs text-emerald-800 mt-1">
              The engagement wrapped up as agreed. Any unspent escrow balance can be refunded prorated (less KSB commission).
            </div>
            <Button
              data-testid="escrow-refund-btn"
              onClick={refund}
              disabled={busy}
              className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Refund unspent balance
            </Button>
          </div>
        </div>
      )}
      {contract.status === "terminated" && (isEmployer || isAdmin) && entries.some((e) => e.status === "terminated") && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">KSB Guarantee window: {features?.guarantee_window_days || 14} days</div>
            <div className="text-xs text-amber-800 mt-1">
              KSB is attempting to place a replacement. If none is agreed by{" "}
              {contract.guarantee_deadline ? new Date(contract.guarantee_deadline).toLocaleDateString("en-KE") : "the deadline"}, request the prorated refund below.
            </div>
            <Button
              data-testid="escrow-refund-btn"
              onClick={refund}
              disabled={busy}
              className="mt-3 bg-amber-600 hover:bg-amber-700 text-white gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Request prorated refund
            </Button>
          </div>
        </div>
      )}

      {/* Pay-month dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-[#F8F9FE]" data-testid="escrow-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Fund this month via M-Pesa</DialogTitle>
            <DialogDescription>
              {formatKES(monthlyAmount)} will be held in the KSB escrow for
              {" "}<strong>{new Date(nextMonthISO).toLocaleDateString("en-KE", { month: "long", year: "numeric" })}</strong>.
              KSB releases {formatKES(Math.round(monthlyAmount / 30))} to the staff each day.
            </DialogDescription>
          </DialogHeader>
          <PayMonthForm defaultPhone={currentUser?.phone} onSubmit={payMonth} busy={busy} amount={monthlyAmount} />
        </DialogContent>
      </Dialog>

      {/* Terminate dialog */}
      <Dialog open={termOpen} onOpenChange={setTermOpen}>
        <DialogContent className="bg-[#F8F9FE]" data-testid="terminate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#B91C1C]">Terminate this contract early?</DialogTitle>
            <DialogDescription>
              Use this for a premature end without full notice. It starts the KSB Placement Guarantee window ({features?.guarantee_window_days || 14} days).
              If no replacement is agreed by then, the unspent, prorated escrow balance will be refunded (less KSB commission).
              If the engagement finished with proper notice, use "Mark as completed" instead.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="terminate-reason"
            rows={4}
            placeholder="Reason (visible to KSB Admin)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="border-[#E4E7F5]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermOpen(false)}>Cancel</Button>
            <Button
              data-testid="terminate-confirm"
              disabled={busy}
              onClick={terminate}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Terminate early
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="bg-[#F8F9FE]" data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-emerald-700">Mark contract as completed?</DialogTitle>
            <DialogDescription>
              Use this when the engagement is ending gracefully with the agreed notice served.
              No replacement guarantee window is required. Any unspent escrow balance can be refunded prorated (less KSB commission).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="complete-notes"
            rows={3}
            placeholder="Optional completion notes (e.g. exit interview highlights)"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            className="border-[#E4E7F5]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button
              data-testid="complete-confirm"
              disabled={busy}
              onClick={complete}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Mark completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayMonthForm({ defaultPhone, onSubmit, busy, amount }) {
  const [phone, setPhone] = useState(defaultPhone || "254");
  return (
    <div className="space-y-4">
      <div>
        <label className="label-eyebrow">M-Pesa Phone</label>
        <input
          data-testid="escrow-phone-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="254712345678"
          className="mt-2 w-full h-11 px-3 rounded-md border border-[#E4E7F5] bg-white"
        />
      </div>
      <Button
        data-testid="escrow-pay-confirm"
        disabled={busy}
        onClick={() => onSubmit({ phone })}
        className="w-full bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white"
      >
        Pay {formatKES(amount)}
      </Button>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-900" },
    terminated: { label: "Terminated · Frozen", cls: "bg-amber-100 text-amber-800" },
    replaced: { label: "Replaced", cls: "bg-indigo-100 text-indigo-800" },
    refunded: { label: "Refunded", cls: "bg-emerald-100 text-emerald-900" },
  };
  const m = map[status] || { label: status, cls: "bg-gray-100 text-gray-800" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${m.cls}`}>{m.label}</span>;
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display font-bold mt-1 ${highlight ? "text-amber-700" : "text-[#0F1E4B]"}`}>{value}</div>
    </div>
  );
}
